import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import React from 'react'
import { NextSeo } from 'next-seo'
import { groq } from 'next-sanity'
import { useRouter } from 'next/router'
import {
  getRouteByPath,
  getClient,
  getSiteConfiguration,
} from '@portfolio/services/sanity-service'
import { generateLocalePaths } from '@portfolio/utils'
import { PageBuilderSection, PageResponse } from '@portfolio/models'
import { seoImageLoader } from '@portfolio/components'
import { DefaultPage } from '@portfolio/page'
import {
  DEFAULT_REVALIDATE_TIME,
  ROUTE_BASE_ARTICLE,
  ROUTE_BASE_CASE_STUDY,
  ROUTE_BASE_SERVICES,
  ORGANIZATION_URL,
} from '@portfolio/configs'
import { captureException } from '@sentry/nextjs'
import { createJobPostingScripts } from '../lib/createJobPostingScripts'

const BREADCRUMB_ROUTES = [
  {
    basePath: ROUTE_BASE_CASE_STUDY,
    name: 'Work',
  },
  {
    basePath: ROUTE_BASE_ARTICLE,
    name: 'Insights',
  },
  {
    basePath: ROUTE_BASE_SERVICES,
    name: 'Services',
  },
]

const Page: NextPage<PageResponse<PageBuilderSection>> = (props) => {
  const { seo, disallowRobots, path, pageBuilder, _updatedAt } = props
  const { asPath } = useRouter()

  const canonicalPath = asPath?.split('?')[0] || path || ''
  const canonicalUrl = `${process.env.BASE_URL}${canonicalPath}`
  const seoImage = seoImageLoader(seo?.ogImage, seo?.ogAltImage)
  const breadcrumbRoute = BREADCRUMB_ROUTES.find(({ basePath }) =>
    canonicalPath.startsWith(`${basePath}/`),
  )

  const breadcrumbItems = [
    {
      position: 1,
      name: 'Home',
      item: ORGANIZATION_URL,
    },
  ]

  if (breadcrumbRoute) {
    breadcrumbItems.push({
      position: 2,
      name: breadcrumbRoute.name,
      item: `${ORGANIZATION_URL}${breadcrumbRoute.basePath}`,
    })
    breadcrumbItems.push({
      position: 3,
      name: seo?.metaTitle,
      item: canonicalUrl,
    })
  } else {
    breadcrumbItems.push({
      position: 2,
      name: seo?.metaTitle,
      item: canonicalUrl,
    })
  }

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item) => ({
      '@type': 'ListItem',
      ...item,
    })),
  }

  const jobPostingJsonLdScripts = createJobPostingScripts({
    pageBuilder,
    pageUpdatedAt: _updatedAt,
  })

  const seoElement = seo ? (
    <NextSeo
      titleTemplate={seo?.titleTemplate}
      nofollow={disallowRobots}
      noindex={disallowRobots}
      title={seo.metaTitle}
      description={seo.metaDescription}
      canonical={canonicalUrl}
      openGraph={{
        url: canonicalUrl,
        type: 'website',
        title: seo.ogTitle,
        description: seo.ogDescription,
        images: [...(seoImage ? [seoImage] : [])],
      }}
      twitter={{
        cardType: 'summary_large_image',
      }}
    />
  ) : (
    <NextSeo
      noindex={disallowRobots}
      nofollow={disallowRobots}
      canonical={canonicalUrl}
    />
  )

  return (
    <>
      {seoElement}
      {canonicalPath !== '/' && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbJsonLd),
          }}
        />
      )}
      {jobPostingJsonLdScripts?.map((jobPostingJsonLdScript) => (
        <script
          key={jobPostingJsonLdScript.key}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(jobPostingJsonLdScript.jsonLd),
          }}
        />
      ))}
      <DefaultPage {...props} />
    </>
  )
}

export const getStaticPaths: GetStaticPaths = async ({ defaultLocale }) => {
  const paths: string[] = await getClient(false).fetch(
    groq`*[
      !(_id in path('drafts.**')) &&
      defined(path.current) &&
      _type != 'hydrogen' &&
      _type != 'aiLab' &&
      path.current != '/insights' &&
      !(path.current match '/insights/*')
    ][].path.current`,
  )

  return {
    paths: generateLocalePaths(paths, defaultLocale),
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps<
  PageResponse<PageBuilderSection[]>
> = async ({ params, locale, preview = false }) => {
  const { paths } = params

  if (typeof paths === 'string') {
    throw new Error('paths must be passed as an array. Received string')
  }

  const path = `/${paths ? paths.join('/') : ''}`

  // Parallelize both queries to reduce total execution time
  const [pageData, siteConfiguration] = await Promise.all([
    getRouteByPath(path, locale, preview).catch((e) => {
      captureException(e)
      throw e
    }),
    getSiteConfiguration(locale, preview),
  ])

  if (!pageData) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...pageData,
      siteConfiguration,
    },
    revalidate: DEFAULT_REVALIDATE_TIME,
  }
}

export default Page
